import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, PabMessage } from "@/services/api";
import { formatDistanceToNow } from "date-fns";
import { parseDateInput } from "@/utils/formatDisplayDate";
import { useAuth } from "@/hooks/useAuth";
import { SendHorizontal } from "lucide-react";
import { MessageBody } from "@/components/MessageBody";
import { TextSkeleton } from "@/components/skeletons";
import { messageSent, messagesOpen } from "@/utils/tracking";

interface MessagesTabProps {
  sessionId: number;
  // v3: advisor identity is the user_id (SHA256 string), not an int PK.
  selectedAdvisorId?: string | null;
}

export const MessagesTab: React.FC<MessagesTabProps> = ({ sessionId, selectedAdvisorId }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [input, setInput] = useState("");
  const trackedMessagesRef = useRef<Set<number>>(new Set());

  // Fetch messages from API
  const {
    data: messagesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["pabMessages", sessionId],
    queryFn: () => api.pabSessions.getMessages(sessionId),
    enabled: !!sessionId,
  });

  // Fetch selected advisors to get recipient count
  const { data: selectedAdvisors } = useQuery({
    queryKey: ["selectedAdvisors", sessionId],
    queryFn: () => api.pabSessions.getSelectedAdvisors(sessionId),
    enabled: !!sessionId,
  });

  const messages = useMemo(() => messagesData || [], [messagesData]);

  // Filter messages if a specific advisor is selected
  // Note: This is a frontend filter. The backend may need to support filtering by advisorId
  const filteredMessages = selectedAdvisorId
    ? messages.filter((msg) => {
        // TODO: This filtering logic is temporary. Once the backend messaging service
        // supports recipient information, update this to accurately filter direct
        // messages between the current user and the selected advisor.
        // Currently, `msg.senderId === user?.id` includes all messages sent by the user,
        // not just those specifically to the selected advisor.
        // If we have selectedAdvisors, check if the sender is the selected advisor
        if (selectedAdvisors) {
          const advisor = selectedAdvisors.find((a) => a.userId === selectedAdvisorId);
          // Show messages from the selected advisor or from the current user (to that advisor)
          return advisor ? msg.senderId === advisor.userId || msg.senderId === user?.id : true;
        }
        return true;
      })
    : messages;

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (body: string) =>
      api.pabSessions.sendMessage(sessionId, body, selectedAdvisorId || undefined),
    onMutate: async (body: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["pabMessages", sessionId] });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData(["pabMessages", sessionId]);

      // Optimistically update to the new value
      const optimisticMessage: PabMessage = {
        id: Date.now(), // Temporary ID
        senderId: user?.id ?? "",
        senderName: user?.fullName ?? "",
        conversationId: sessionId,
        body: body,
        status: "sending",
        createdAt: new Date().toISOString(),
        isRead: true,
      };

      queryClient.setQueryData(["pabMessages", sessionId], (old: PabMessage[] = []) => [
        ...old,
        optimisticMessage,
      ]);

      // Return a context object with the snapshotted value
      return { previousMessages, messageBody: body };
    },
    onSuccess: (sentMessage, body) => {
      // Track message_sent event
      if (user?.id && selectedAdvisors) {
        // If sending to a specific advisor, recipient_count is 1 (just that advisor)
        // Otherwise, recipient_count = all advisors
        const recipientCount = selectedAdvisorId ? 1 : selectedAdvisors.length;

        messageSent({
          message_id: sentMessage.id,
          thread_id: sessionId,
          sender_user_id: user.id,
          recipient_count: recipientCount,
          channel: "in_app",
          message_type: "text",
          has_attachments: false,
          message_length: body.length,
        });
      }
    },
    onError: (_err, _body, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(["pabMessages", sessionId], context?.previousMessages);
    },
    onSettled: () => {
      // Always refetch after error or success to sync with server
      queryClient.invalidateQueries({ queryKey: ["pabMessages", sessionId] });
    },
  });

  // Track messages_open for messages from others when first viewed
  useEffect(() => {
    if (!messages.length || !user?.id || isLoading) return;

    messages.forEach((message) => {
      // Only track messages not sent by current user and not already tracked
      if (message.senderId !== user.id && !trackedMessagesRef.current.has(message.id)) {
        const now = new Date();
        const messageCreatedAt = parseDateInput(message.createdAt) ?? now;
        const openedAfterMinutes = Math.floor((now.getTime() - messageCreatedAt.getTime()) / (1000 * 60));

        messagesOpen({
          sender_id: message.senderId,
          message_id: message.id,
          thread_id: sessionId,
          viewer_user_id: user.id,
          opened_after_minutes: openedAfterMinutes,
        });

        // Mark this message as tracked
        trackedMessagesRef.current.add(message.id);
      }
    });
  }, [messages, user?.id, sessionId, isLoading]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    sendMessageMutation.mutate(input);
    setInput("");
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full px-4 py-6 space-y-4 bg-white overflow-y-auto">
        {Array(3)
          .fill(0)
          .map((_, i) => (
            <div key={i} className={i % 2 === 0 ? "flex justify-start" : "flex justify-end"}>
              <div className="max-w-[70%] sm:max-w-[60%] md:max-w-[50%]">
                <TextSkeleton
                  width={i % 2 === 0 ? "120px" : "140px"}
                  lineHeight="0.875rem"
                  className="mb-2"
                />
                <div className="bg-gray-100 rounded-xl p-3 sm:p-4">
                  <TextSkeleton lines={2} width="100%" lastLineWidth="70%" lineHeight="0.875rem" />
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-red-500 text-center">Error loading messages, contact support</div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 bg-white">
        {selectedAdvisorId && selectedAdvisors && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm text-blue-800">
              <strong>Direct message mode:</strong> Messages will be sent only to{" "}
              {selectedAdvisors.find((a) => a.userId === selectedAdvisorId)?.name || "this expert"}
            </div>
          </div>
        )}
        {filteredMessages.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <div className="text-gray-500 text-center">
              {selectedAdvisorId ? "No messages with this expert yet" : "No messages yet"}
            </div>
          </div>
        ) : (
          filteredMessages.map((msg) => {
            const isCurrentUser = user?.id === msg.senderId;
            return (
              <div key={msg.id} className="flex gap-3">
                {!isCurrentUser && (
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-base">
                    {msg.senderName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`font-semibold text-sm ${isCurrentUser ? "text-gray-700" : "text-gray-900"}`}
                    >
                      {isCurrentUser ? "You" : msg.senderName}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(parseDateInput(msg.createdAt) ?? new Date(), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <div
                    className={`mt-1 text-sm text-gray-700 ${isCurrentUser ? "bg-gray-50 rounded-lg px-3 py-2" : ""}`}
                  >
                    {/* This tab reads the same PAB thread the message drawers
                        write to, so a message formatted there has to render
                        here too — printed raw it would show its markdown
                        markers. MessageBody also linkifies bare URLs, which is
                        what the local helper did on its own. */}
                    <MessageBody text={msg.body} />
                  </div>
                </div>
                {isCurrentUser && (
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-base">
                    {msg.senderName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Email notification notice */}
      {!selectedAdvisorId && (
        <div className="sticky bottom-20 left-0 right-0 z-10 mx-4 mb-2">
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm text-primary-800">
            <div className="flex items-start gap-2">
              <svg
                className="w-4 h-4 mt-0.5 text-primary-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                <strong>Note:</strong> All messages will be sent to everyone's email inbox. Please be
                mindful of message frequency.
              </span>
            </div>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSend}
        className="sticky bottom-0 left-0 right-0 z-10 p-4 border-t bg-white flex items-center gap-2"
      >
        <input
          type="text"
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Type a message"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={100}
        />
        <button
          type="submit"
          className="p-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center"
          aria-label="Send message"
        >
          <SendHorizontal className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
};
